import { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { nanoid } from 'nanoid';
import { useDataHelper } from './dataHelper';
import { usePubnabLambdaServices } from './pubnubLambdaHelper';
import PubNub from 'pubnub';
import { PUBNUB_PUBLISH_KEY, PUBNUB_SUBSCRIBE_KEY } from './constants';

const createHandlerSetter = (handlerValue) => {
  const handlerRef = useRef(handlerValue);

  // since useRef accepts an initial-value only, this is needed to make sure
  handlerRef.current = handlerValue;

  const setHandler = useCallback((nextCallback) => {
    handlerRef.current = nextCallback;
  });

  return [handlerRef, setHandler];
};

const useChat = ({
  userUuid,
  userMetadata,
  channel,
  onSignal,
  showLoader,
  hideLoader,
}) => {
  //Hooks
  const pubnabService = usePubnabLambdaServices({}); //without config, only for client login
  const { parseFromPubnubTimeToken } = useDataHelper();

  //States
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [fetchedMessages, setFetchedMessages] = useState([]);
  const [moderatedMessages, setModeratedMessages] = useState();
  const [messages, setMessages] = useState([]);
  const [occupants, setOccupants] = useState([]);
  const [hasBeenBanned, setHasBeenBanned] = useState(false);
  const [client, setClient] = useState();
  const [clientData, setClientData] = useState();

  // set signal callback
  const [onSignalRef, setOnSignalRef] = createHandlerSetter(onSignal);

  useEffect(() => {
    if (clientData) initChatClient();
  }, [clientData]);

  useEffect(() => {
    setOnSignalRef(onSignal);
  }, [onSignal]);

  useEffect(() => {
    if (channel && userUuid) {
      setClientData({
        uuid: getUUID(userUuid),
        channel: channel,
      });
    }
  }, [userUuid, channel]);

  useEffect(() => {
    if (client && userMetadata) {
      login();
    }
    //return client && logout();
  }, [client, userMetadata]);

  useEffect(() => {
    if (!client || !channel || !isSubscribed) return;
    fetchMessage();
  }, [client, channel, isSubscribed]);

  useEffect(() => {
    if (!client || !channel || !isSubscribed) return;
    applyModeration();
  }, [client, channel, isSubscribed, fetchedMessages]);

  useEffect(() => {
    if (client && channel && isSubscribed && moderatedMessages) {
      const augmentedMsg = addTimeSeparatorToMessages(moderatedMessages);
      setMessages(augmentedMsg);
    }
  }, [client, channel, isSubscribed, moderatedMessages]);

  const initChatClient = async () => {
    const pubNubConfig = {
      publishKey: PUBNUB_PUBLISH_KEY,
      subscribeKey: PUBNUB_SUBSCRIBE_KEY,
      uuid: clientData.uuid,
      autoNetworkDetection: true, // enable for non-browser environment automatic reconnection
      restore: true, // enable catchup on missed messages
    };
    const clientWithoutAuth = new PubNub(pubNubConfig);

    const loginResponse = await pubnabService.login(clientData);
    if (loginResponse.message.error) {
      setHasBeenBanned(true);
    }

    clientWithoutAuth.setAuthKey(loginResponse.authKey);
    //now client has authKey
    setClient(clientWithoutAuth);
  };

  const fetchMessagesFromServer = async () => {
    const serverMessages = await new Promise((resolve, reject) => {
      client.fetchMessages(
        { channels: [channel], count: 50 },
        (status, response) => {
          if (status.error) reject(status.error);
          const nextMessages = response?.channels[channel];
          resolve(nextMessages || []);
        }
      );
    });
    return serverMessages;
  };

  const fetchOlderMessages = async (callback) => {
    client.fetchMessages(
      {
        channels: [channel],
        count: 50,
        start: moderatedMessages?.[0]?.timetoken || pubNubTimeToken(),
      },
      (status, response) => {
        if (status.error) return;
        const nextMessages = response?.channels[channel];
        if (nextMessages) {
          setFetchedMessages([...nextMessages, ...moderatedMessages]);
        }
        callback?.();
      }
    );
  };
  const fetchMessageActionsFromServer = async () => {
    const _messageActions = await new Promise((resolve, reject) => {
      client.getMessageActions(
        {
          channel: channel,
        },
        (status, response) => {
          if (status.error) reject(status.error);
          if (response) {
            resolve(response?.data);
          }
        }
      );
    });
    return _messageActions;
  };

  const addTimeSeparatorToMessages = (moderatedMessages) => {
    const createTimeSeparator = (date) => ({
      channel: channel,
      message: {
        author: channel,
        content: {
          date,
          type: 'timeSeparator',
        },
      },
    });

    const augmentedMsg = moderatedMessages.reduce((arr, curr, idx) => {
      const currentDate = parseFromPubnubTimeToken(curr.timetoken);
      if (idx > 0) {
        const pre = arr[arr.length - 1];
        const currDay = format(currentDate, 'yyyy-MM-dd');
        const prevDay = format(
          parseFromPubnubTimeToken(pre.timetoken),
          'yyyy-MM-dd'
        );
        if (prevDay !== currDay) {
          arr.push(createTimeSeparator(currentDate));
        }
      } else {
        arr.push(createTimeSeparator(currentDate));
      }
      arr.push(curr);
      return arr;
    }, []);

    return augmentedMsg;
  };

  const fetchMessage = async () => {
    const _fetchedMessages = await fetchMessagesFromServer();
    setFetchedMessages(_fetchedMessages);
  };

  const applyModeration = async () => {
    const actions = await fetchMessageActionsFromServer();
    const _moderatedMessages = moderateMessages(fetchedMessages, actions);
    setModeratedMessages(_moderatedMessages);
  };

  const moderateMessages = (tempMessages, moderationActions) => {
    let _moderatedMessages = tempMessages;
    moderationActions.map((action) => {
      _moderatedMessages = applyActionsToMessages(action, _moderatedMessages);
    });
    return _moderatedMessages;
  };

  const applyActionsToMessages = (action, list) => {
    if (action?.type === 'moderated' && list) {
      const { messageTimetoken, value } = action;
      const msgIdx = list.findIndex((m) => {
        return m.timetoken === messageTimetoken && !m.isModerated;
      });
      if (msgIdx >= 0) {
        const messageObject = list[msgIdx];
        messageObject.message.content.text = value;
        //If it has been moderated, we add a flag to intercept the graphic part
        messageObject.isModerated = true;
        list[msgIdx] = messageObject;
      }
    }
    return list;
  };

  const pushNewMessage = useCallback((message) => {
    if (message) {
      setModeratedMessages((pre) => [...pre, message]);
    }
  }, []);

  const hanldeMessageActionSignal = useCallback((action) => {
    if (action?.data) {
      setModeratedMessages((pre) => {
        return moderateMessages([...pre], [action.data]);
      });
    }
  }, []);

  const checkBanSignal = useCallback(
    (signal) => {
      if (clientData) {
        if (signal?.message?.type === 'ban' && clientData) {
          if (signal.message.uuid === clientData.uuid)
            setHasBeenBanned(signal.message.value);
        }
      } else {
        setHasBeenBanned(true);
      }
    },
    [clientData]
  );

  const handleStatusEvent = useCallback((statusEvent) => {
    if (statusEvent.error) {
      console.log(statusEvent);
      resetClient();
    } else {
      switch (statusEvent.operation) {
        case 'PNSubscribeOperation':
          {
            //In questo modo siamo sicuri di esserci sottoscritti al canale
            if (statusEvent.subscribedChannels?.includes(channel)) {
              setIsSubscribed(true);
              hideLoader?.();
            }
          }
          break;
      }
    }
  }, []);

  const resetClient = async () => {
    if (client) {
      client.removeListener(listeners);
      client.unsubscribe({ channels: [channel] });
    }
    setIsSubscribed(false);
    await logout();
  };

  const listeners = {
    status: (statusEvent) => {
      handleStatusEvent(statusEvent);
    },
    message: (newMessage) => {
      pushNewMessage(newMessage);
    },
    messageAction: (action) => {
      hanldeMessageActionSignal(action);
    },
    signal: (s) => {
      s && onSignalRef?.current && onSignalRef?.current(s);
      checkBanSignal(s);
    },
    presence: (p) => {
      //console.log('🚀 ~ file: useChat.js ~ line 244 ~ useEffect ~ p', p);
      updateOccupants([p]);
    },
  };

  useEffect(() => {
    if (channel && client) {
      subscribeClient();
      return resetClient;
    }
  }, [client, channel]);

  const subscribeClient = async () => {
    showLoader?.();
    const timetoken = pubNubTimeToken();
    client.addListener(listeners);
    await client.subscribe({
      channels: [channel],
      timetoken: timetoken,
      withPresence: true,
    });
    //il termine della subscription avverrà quando arriverà il cambio di status
  };

  const pubNubTimeToken = () => {
    const timetoken = Math.floor(Date.now() * 10000);
    return timetoken;
  };

  const publish = (text, callback) => {
    client.publish(
      {
        channel: [channel],
        message: {
          id: nanoid(),
          author: getUUID(userUuid),
          content: text,
        },
        sender: userMetadata,
      },
      async (status, response) => {
        //        await client.auth();
        callback?.({ status, response });
      }
    );
  };

  const publishSignal = (text, callback) => {
    client.signal(
      {
        channel: [channel],
        message: text,
      },
      (status, response) => {
        callback?.({ status, response });
      }
    );
  };

  const logout = async () => {
    await changeUserState({ isOnline: false });
  };

  const login = async () => {
    await changeUserState({ isOnline: true });
  };

  const changeUserState = async ({ isOnline }) => {
    const input = {
      state: { isOnline: isOnline, user: userMetadata },
      channels: [channel],
    };
    try {
      await client.setState(input);
      updatePresence();
    } catch (e) {
      console.error(e);
    }
  };

  const updatePresence = async () => {
    const presenceRes = await client.hereNow({
      channels: [channel],
      includeState: true,
      includeUUIDs: true,
    });

    if (presenceRes)
      //controllo gli utenti attivi poco dopo essermi connesso così da essere sicuro di recuperare le utenze
      setTimeout(() => {
        updateOccupants(presenceRes.channels[channel].occupants);
      }, 2500);
  };

  const updateOccupants = (presenceResponses) => {
    // console.log(
    //   '🚀 ~ file: useChat.js ~ line 370 ~ updateOccupants ~ presenceResponse',
    //   presenceResponse
    // );
    setOccupants((exOccupants) => {
      let newOccupants = [...exOccupants];
      presenceResponses.map((presenceResponse) => {
        const { uuid, state } = presenceResponse;
        if (state) {
          if (newOccupants) {
            const occupantIdx = newOccupants.findIndex((o) => o.uuid === uuid);
            const obj = { uuid: uuid, state: state };
            if (occupantIdx !== -1) {
              newOccupants[occupantIdx] = obj;
            } else {
              newOccupants.push(obj);
            }
          }
        }
      });
      return newOccupants;
    });
  };

  const fetchUsersMetadata = async (uuid) => {
    try {
      const res = await new Promise((resolve, reject) => {
        client.objects.getUUIDMetadata(
          {
            uuid: uuid,
            include: {
              customFields: true,
            },
          },
          (status, response) => {
            if (status.error) reject(status.errorData);
            else {
              const _response = {
                isBanned: response.data.custom[`channel_${channel}_is_banned`],
              };
              resolve(_response);
            }
          }
        );
      });
      return res;
    } catch (e) {
      console.log(e);
      return null;
    }
  };

  return {
    client,
    messages,
    occupants,
    isSubscribed,
    hasBeenBanned,
    getServerTime: pubNubTimeToken,
    publish,
    publishSignal,
    fetchOlderMessages,
    fetchUsersMetadata,
    logout,
  };
};

/**
 * Serve a comporre un uuid digeribile da pubnub
 * @param {*} origin
 * @returns
 */
const getUUID = (origin) => {
  const arr = origin.split('-');
  const uuid = [...arr.slice(1, 2), ...arr.slice(4, 5)].join('');
  return uuid;
};

export { useChat, getUUID };
